import React, { useRef } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import mockup from "../assets/Mobile.png";
import emblemLogo from "../assets/logo-vertical.png";
import { useTranslation } from "react-i18next";
import { motion, useScroll, useTransform } from "framer-motion";

const Home = ({ Brands }: any) => {
  const isMobile = useMediaQuery("(max-width: 600px)");
  const ref = useRef(null);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language.startsWith("ar");

  const { scrollY } = useScroll();
  // Subtle scroll parallax for the left app mockup
  const y = useTransform(scrollY, [0, 600], [0, 40]);
  const rotate = useTransform(scrollY, [0, 600], [5, 2]);
  const scale = useTransform(scrollY, [0, 600], [1, 0.98]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: [0.25, 1, 0.5, 1] as const },
    },
  };

  return (
    <Box
      ref={ref}
      sx={{
        minHeight: "100vh",
        width: "100%",
        background: "transparent",
        color: "white",
        px: "5%",
        py: { xs: "2%", md: "8%" },
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* Glowing Backdrop Lights */}
      <Box
        sx={{
          position: "absolute",
          width: { xs: "300px", md: "500px" },
          height: { xs: "300px", md: "500px" },
          background: "radial-gradient(circle, rgba(255, 174, 0, 0.12) 0%, rgba(255, 174, 0, 0) 70%)",
          filter: "blur(50px)",
          top: "10%",
          left: isRTL ? "auto" : "5%",
          right: isRTL ? "5%" : "auto",
          zIndex: 1,
          pointerEvents: "none",
          animation: "bg-glow-float 12s ease-in-out infinite",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          width: { xs: "300px", md: "600px" },
          height: { xs: "300px", md: "600px" },
          background: "radial-gradient(circle, rgba(255, 174, 0, 0.08) 0%, rgba(255, 174, 0, 0) 70%)",
          filter: "blur(60px)",
          bottom: "10%",
          right: isRTL ? "auto" : "8%",
          left: isRTL ? "8%" : "auto",
          zIndex: 1,
          pointerEvents: "none",
          animation: "bg-glow-float 18s ease-in-out infinite alternate-reverse",
        }}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column-reverse" : isRTL ? "row" : "row-reverse",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          maxWidth: "1200px",
          mx: "auto",
          gap: { xs: 2, md: 4 },
          mt: { xs: 7, md: 4 },
          position: "relative",
          zIndex: 2,
        }}
      >
        {/* Left Side: Mockup Image */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            width: isMobile ? "65%" : "42%",
            maxWidth: isMobile ? "220px" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            y,
            rotate,
            scale,
            marginTop: isMobile ? "0px" : "100px",
          }}
        >
          <Box
            component="img"
            src={mockup}
            alt="TowHelp App Mockup"
            sx={{
              width: "100%",
              height: "auto",
              transition: "transform 0.5s ease",
              "&:hover": {
                transform: "scale(1.03)",
              }
            }}
          />
        </motion.div>

        {/* Right Side: Text & Actions */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          dir={isRTL ? "rtl" : "ltr"}
          style={{
            width: isMobile ? "100%" : "53%",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: isMobile ? "center" : isRTL ? "center" : "flex-start",
          }}
        >
          {/* Logo emblem above the title */}
          <motion.div
            variants={itemVariants}
            style={{ 
              display: "flex", 
              justifyContent: isMobile ? "center" : isRTL ? "center" : "flex-start", 
              width: "100%", 
              marginBottom: isMobile ? "-15px" : "-30px" 
            }}
          >
            <Box
              component="img"
              src={emblemLogo}
              alt="TowHelp Emblem"
              sx={{
                height: { xs: 90, sm: 140, md: 240 },
                mt: { xs: 1, sm: 3, md: 6 },
                filter: "drop-shadow(0 0 15px rgba(255, 174, 0, 0.3))",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                }
              }}
            />
          </motion.div>
          <motion.div
            variants={itemVariants}
            style={{ margin: 0, width: "100%", textAlign: isMobile ? "center" : isRTL ? "center" : "left" }}
          >
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontSize: { xs: "1.85rem", sm: "2.6rem", md: "3.8rem", lg: "4.2rem" },
                fontWeight: 900,
                lineHeight: 1.2,
                background: "linear-gradient(135deg, #FFAE00 0%, #FF9100 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: { xs: 1.5, md: 3 },
                maxWidth: "100%",
                wordBreak: "break-word",
                fontFamily: "Poppins, Ozen",
                textShadow: "0 0 40px rgba(255, 174, 0, 0.15)",
                whiteSpace: "pre-line",
                textAlign: isMobile ? "center" : isRTL ? "center" : "left",
              }}
            >
              {t("roadsideHelpTitle")}
            </Typography>
          </motion.div>

          <motion.div
            variants={itemVariants}
            style={{ margin: 0, width: "100%", display: "flex", justifyContent: isMobile ? "center" : isRTL ? "center" : "flex-start" }}
          >
            <Typography
              variant="h6"
              sx={{
                width: isMobile ? "90%" : "85%",
                fontWeight: 400,
                opacity: 0.8,
                color: "#e0e0e0",
                fontSize: { xs: "0.85rem", md: "1.15rem" },
                mb: { xs: 2.5, md: 5 },
                lineHeight: 1.7,
                textAlign: isMobile ? "center" : isRTL ? "center" : "left",
                whiteSpace: "pre-line",
              }}
            >
              {t("roadsideHelpDescription")}
            </Typography>
          </motion.div>

          <motion.div
            variants={itemVariants}
            style={{
              display: "flex",
              gap: 20,
              flexWrap: "wrap",
              justifyContent: isMobile ? "center" : isRTL ? "center" : "flex-start",
              width: "100%",
            }}
          >
            <Box
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (Brands?.appleStoreLink) {
                  window.open(Brands.appleStoreLink, "_blank", "noopener,noreferrer");
                }
              }}
              sx={{
                display: "inline-block",
                borderRadius: "10px",
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 4px 15px rgba(0,0,0,0.4)",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.03)",
                  boxShadow: "0 10px 25px rgba(255,174,0,0.15)",
                },
              }}
            >
              <Box
                component="img"
                src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83"
                alt="App Store"
                sx={{ height: 42, display: "block" }}
              />
            </Box>

            <Box
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (Brands?.googlePlayStoreLink) {
                  window.open(Brands.googlePlayStoreLink, "_blank", "noopener,noreferrer");
                }
              }}
              sx={{
                display: "inline-block",
                borderRadius: "10px",
                overflow: "hidden",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 4px 15px rgba(0,0,0,0.4)",
                "&:hover": {
                  transform: "translateY(-4px) scale(1.03)",
                  boxShadow: "0 10px 25px rgba(255,174,0,0.15)",
                },
              }}
            >
              <Box
                component="img"
                src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                alt="Google Play"
                sx={{ height: 42, display: "block" }}
              />
            </Box>
          </motion.div>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Home;

